import { Grid, GridColumn as Column, GridColumnMenuGroup, GridColumnReorderEvent, GridDataStateChangeEvent, GridSortChangeEvent } from '@progress/kendo-react-grid'
import React, { useEffect, useMemo } from 'react'
import { Api } from "../../redux/api/BaseApi";
import { AssetApi } from "../../redux/api/AssetApi";
import { UserApi } from "../../redux/api/UserApi";
import { GridColumnOptions } from './GridColumnOptions';
import { nanoid } from 'nanoid';
import { Checkbox, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { CompositeFilterDescriptor, process, SortDescriptor, State } from '@progress/kendo-data-query';
import { IAsset } from '../../types/Asset/IAsset';
import { ISetting } from '../../types/User/ISetting';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svg from '@progress/kendo-svg-icons';
import { ITicketContact } from '../../types/Ticket/ITicketContact';
import { Link } from 'react-router-dom';


export const AssetGrid = (props: AssetGridProps) => {
    const { data: userGridState } = UserApi.useGetSettingQuery(props.persistedSettingId, { skip: !props.persistedSettingId })
    const [saveSetting] = UserApi.useSaveSettingMutation()
    const { data: userProfile } = Api.useGetUserProfileQuery();
    const currentIds = props.selectedAssets
    const { data } = AssetApi.useGetAssetsQuery({ companyId: props.companyId, locationId: props.locationId, contactId: props.locationId ? 0 : props.contactId }, { skip: !props.companyId })
    //const bindData = props.locationId ? data?.filter(d => d.Contact_Id !== props.contactId) : data
    const [bindData, setBindData] = React.useState<IAsset[]>(null)
    const [initialSort, setInitialSort] = React.useState<boolean>(false)

    useEffect(() => {
        if (data) {
            let newData = data?.slice();
            // if (currentIds?.length && !initialSort) {

            //     //Sort data and put selected assets at the top ONLY the first time this loads
            //     newData = newData.sort((a, b) => {
            //         if (currentIds?.indexOf(a.ID) > -1 && currentIds?.indexOf(b.ID) === -1) return -1
            //     })
            //     console.log('newDsetBindData sorted');
            //     setInitialSort(true)
            // }
            // console.log('newDsetBindData', currentIds?.length, initialSort);
            setBindData(props.locationId ? newData?.filter(d => d.Contact_Id !== props.contactId) : newData)
        }
    }, [data, currentIds]);


    const columns = [
        {
            title: userProfile?.Phrases.Contact,
            field: "Contact_Name",
            show: props.showContactLinkColumn,
            filter: "text",
            orderIndex: 0
        },
        {
            title: userProfile?.Phrases.Device,
            field: "Device_Name",
            show: true,
            filter: "text",
            orderIndex: 0
        },
        {
            title: userProfile?.Phrases.Manufacturer,
            field: "Manuf_Name",
            show: true,
            filter: "text",
            orderIndex: 1
        },
        {
            title: userProfile?.Phrases.Model,
            field: "Model_Name",
            show: true,
            filter: "text",
            orderIndex: 2
        },
        {
            title: userProfile?.Phrases.SerialNumber,
            field: "Serial_Number",
            show: true,
            filter: "text",
            orderIndex: 3
        },
        {
            title: userProfile?.Phrases.Memory,
            field: "Memory",
            show: true,
            filter: "text",
            orderIndex: 4
        },
        {
            title: userProfile?.Phrases.Processor,
            field: "Processor",
            show: false,
            filter: "text",
            orderIndex: 5
        },
        {
            title: userProfile?.Phrases.AssetCustom1,
            field: "Custom1",
            show: false,
            filter: "text",
            orderIndex: 6
        },
        {
            title: userProfile?.Phrases.AssetCustom2,
            field: "Custom2",
            show: false,
            filter: "text",
            orderIndex: 7
        },
        {
            title: userProfile?.Phrases.AssetCustom3,
            field: "Custom3",
            show: false,
            filter: "text",
            orderIndex: 8
        },
        {
            title: userProfile?.Phrases.AssetCustom4,
            field: "Custom4",
            show: false,
            filter: "text",
            orderIndex: 9
        },
        {
            title: userProfile?.Phrases.AssetCustom5,
            field: "Custom5",
            show: false,
            filter: "text",
            orderIndex: 10
        },
    ];

    //The user's saved grid state and columns
    const userStateFromSettings = userGridState?.Big_String_Value ? JSON.parse(userGridState?.Big_String_Value) : undefined;
    const userState = (userStateFromSettings ? userStateFromSettings : { state: { take: 20, skip: 0 }, columns: null }) as { state: State, columns: [{ field: string, orderIndex: number }] }

    //The user's chosen columns
    const userColumns = columns.map((c) => {
        let userColIndex = userState?.columns?.map(uc => uc.field).indexOf(c.field)

        return (userState?.columns?.length ?
            userColIndex > -1 ? { ...c, show: true, orderIndex: userState?.columns[userColIndex].orderIndex } : { ...c, show: false }
            : c)
    })
    const [dataState, setDataState] = React.useState<State>(userState?.state || {});
    const [stateColumns, setStateColumns] = React.useState(userColumns);

    //Update the grid state when the user's settings are loaded.
    useEffect(() => {
        //never override the page number or filters
        setDataState({ ...userState?.state, skip: dataState?.skip, filter: dataState?.filter, sort: null })
        setStateColumns(userColumns)
    }, [userGridState])


    const onColumnsSubmit = (columnsState) => {
        setStateColumns(columnsState);

        persistUserState(dataState, columnsState);
    };

    const contactSelected = (e: IAsset) => {
        let newArray = [...props.associatedContacts];
        let found = newArray?.filter(a => a.Contact_Id === e.Contact_Id)
        if (found?.length)
            newArray.splice(newArray?.indexOf(found[0]), 1)
        else
            newArray.push({ Contact_Id: e.Contact_Id } as ITicketContact);

        props.onContactChange({ value: newArray });
    }

    const assetSelected = (e: IAsset) => {
        let newArray = data.filter(ass => props.selectedAssets?.indexOf(ass.ID) > -1);
        let found = newArray?.filter(a => a.ID === e.ID)
        if (found?.length)
            newArray.splice(newArray?.indexOf(found[0]), 1)
        else
            newArray.push(e);

        props.onChange({ value: newArray });
    };

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        setDataState(event.dataState);

        persistUserState(event.dataState, null);
    };

    const persistUserState = (dataState: State, columnsState: any[]) => {
        //Save to user templateSettings
        if (props.persistedSettingId) {
            //, filter: undefined
            saveSetting({ ID: 0, Contact_Id: userProfile.Id, Setting_Id: props.persistedSettingId, Big_String_Value: JSON.stringify({ state: { ...dataState, filter: undefined, skip: 0 }, columns: (columnsState || stateColumns).filter(c => c.show).map(m => { return { field: m.field, orderIndex: m.orderIndex } }) }) } as ISetting)
        }
    }

    const columnReorder = (e: GridColumnReorderEvent) => {
        let newColumns = e.columns.filter(f => f.field).map(c => { return { title: c.title, field: c.field, filter: c.filter, orderIndex: c.orderIndex, show: true } })
        setStateColumns(newColumns);
        persistUserState(dataState, newColumns);
    }

    const filterChanged = (e: TextBoxChangeEvent) => {
        let newFilter = {
            logic: "or",
            operator: "eq",
            filters: e.value ? columns.map(c => { return { field: c.field, operator: "contains", value: e.value } }) : []
        } as CompositeFilterDescriptor
        let newState = { ...dataState, filter: newFilter, skip: 0 }
        setDataState(newState)

        persistUserState(newState, null);
    }

    const columnsToRender = stateColumns?.map(
        (column, idx) =>
            column.show && (
                <Column
                    key={idx}
                    orderIndex={column.orderIndex}
                    field={column.field}
                    title={column.title}
                    //filter={column.filter}
                    columnMenu={(props) => (
                        <>
                            <GridColumnMenuGroup {...props} />
                            <GridColumnOptions
                                {...props}
                                columns={userColumns}
                                onColumnsSubmit={onColumnsSubmit}
                            />
                        </>
                    )}
                />
            )
    )

    return (
        <>
            <div className="flex w-full mb-3">
                <TextBox style={{ width: '70%' }} type={"text"} onChange={filterChanged} placeholder='Filter' />
            </div>
            <Grid className='asset-grid'
                data={process(bindData || [], dataState)}
                {...dataState}
                onDataStateChange={dataStateChange}
                onColumnReorder={columnReorder}
                sortable={true}
                groupable={true}
                pageable={true}
                reorderable={true}
                pageSize={50}
            >
                {/* <Column width={100} locked={true} reorderable={false}
                    key={nanoid()}
                    field="Contact_Id" /> */}
                <Column width={50} locked={true} reorderable={false}
                    key={nanoid()}
                    field=""
                    headerCell={(e) => <SvgIcon icon={svg.linkIcon} className='m-1' />}
                    cell={(e) => {
                        if (e.rowType === 'groupHeader') return null;
                        return (
                            <td style={{ textAlign: 'center' }} className='chk'>
                                <Checkbox checked={currentIds?.indexOf(e.dataItem["ID"]) > -1} onChange={() => assetSelected(e.dataItem)} />
                            </td>)
                    }}
                />

                {props.showContactLinkColumn && <Column width={50} locked={true} reorderable={false}
                    key={nanoid()}
                    field=""
                    headerCell={(e) => <div title='Add associated contact'><SvgIcon icon={svg.plusOutlineIcon} className='m-1' /></div>}
                    cell={(e) => {
                        if (e.rowType === 'groupHeader') return null;
                        return (
                            <td style={{ textAlign: 'center' }} className='chk'>
                                <Checkbox disabled={!e.dataItem["Contact_Id"]} title='Add associated contact' checked={props.associatedContacts?.find(a => a.Contact_Id === e.dataItem["Contact_Id"]) !== undefined} onChange={() => contactSelected(e.dataItem)} />
                            </td>)
                    }}
                />}
                {columnsToRender}
                <Column orderIndex={100} width={50} locked={true} reorderable={false}
                    key={nanoid()}
                    field=""
                    cell={(e) => {
                        if (e.rowType === 'groupHeader') return null;
                        return (
                            <td style={{ textAlign: 'center' }}>
                                <Link target='_blank' title='View asset' to={'/deeplink?url=/Asset_Mgr/Asset.aspx?Asset_Id=' + e.dataItem["ID"]} > <SvgIcon icon={svg.hyperlinkOpenSmIcon} className='m-1' /></Link>
                            </td>)
                    }}
                />
            </Grid>
        </>
    )
}



export interface AssetGridProps {
    persistedSettingId?: number
    contactId?: number
    locationId?: number
    companyId?: number
    onChange?: (e: { value: IAsset[] }) => void
    onContactChange?: (e: { value: ITicketContact[] }) => void
    selectedAssets: number[],
    showContactLinkColumn?: boolean
    associatedContacts?: ITicketContact[]
}